import { useCallback, useEffect, useState } from 'react';
import { Container, Stack, Typography, Button, Grid, tableCellClasses } from '@mui/material';
import { Box } from '@mui/system';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';

import CustomDate from '../../components/CustomDate';
import CustomInput from '../../components/CustomInput';
import PageTitle from '../../components/PageHeading';
import { fDate } from '../../utils/formatTime';
import { fIndianAmount } from '../../utils/formatNumber';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { NOT_AVAILABLE } from '../Global/Constants';
import { StyledTableRow } from '../../utils/StyleTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getBankDropdownData,
  getLoanTypeData,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import { isValidValue, normalizeValue } from '../../utils/CommonFunction';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';
import { ReceiveApi } from '../../utils/Apis';
import inputValidation from '../../utils/globalValidation';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

function ViewMultipleFile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { selectedItem } = location?.state || {};
  const { LoginData } = useLoginSelector();
  const { LoanTypeDrop, BankDrop } = useGlobalSelector();

  const [validationErrors, setValidationErrors] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    invoiceAmount: '',
    cGstAmount: '',
    iGstAmount: '',
    sGstAmount: '',
    totalAmount: '',
    invoiceDueDate: ''
  });

  const {
    control,
    watch,
    handleSubmit: handleReduxSubmit,
    formState: { errors },
    setValue
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      invoiceNumber: '',
      invoiceDate: '',
      invoiceAmount: '',
      cGstAmount: '',
      iGstAmount: '',
      sGstAmount: '',
      totalAmount: '',
      invoiceDueDate: ''
    }
  });

  const formValues = watch();

  // console.log('sss selectedItem', selectedItem);

  const receivableIds = selectedItem.reduce((acc, item) => {
    if (item?.receivableId) acc.push(item.receivableId);
    return acc;
  }, []);

  useEffect(() => {
    const misInvoiceNumbers = (selectedItem || []).map((item) => item?.misInvoiceNumber);
    const firstMisInvoiceNumber = misInvoiceNumbers[0];
    if (
      firstMisInvoiceNumber &&
      misInvoiceNumbers.every((misInvoiceNumber) => misInvoiceNumber === firstMisInvoiceNumber)
    ) {
      setValue('invoiceNumber', firstMisInvoiceNumber);
    }
  }, [selectedItem, setValue]);

  // ✅ Auto-populate GST when invoice amount is entered
  useEffect(() => {
    const invoiceAmount = parseFloat(formValues.invoiceAmount) || 0;

    if (invoiceAmount > 0) {
      const cgstValue = parseFloat((invoiceAmount * 0.09).toFixed(2));
      const sgstValue = parseFloat((invoiceAmount * 0.09).toFixed(2));
      const igstValue = parseFloat((invoiceAmount * 0.18).toFixed(2));

      setValue('cGstAmount', cgstValue);
      setValue('sGstAmount', sgstValue);
      setValue('iGstAmount', igstValue);
    } else {
      // Reset GST fields when invoice amount is empty
      setValue('cGstAmount', '');
      setValue('sGstAmount', '');
      setValue('iGstAmount', '');
      setValue('totalAmount', '');
    }
  }, [formValues.invoiceAmount, setValue]);

  // ✅ Auto-calculate total amount based on GST selection
  useEffect(() => {
    const invoiceAmount = parseFloat(formValues.invoiceAmount) || 0;
    const cGstAmount = parseFloat(formValues.cGstAmount) || 0;
    const sGstAmount = parseFloat(formValues.sGstAmount) || 0;
    const iGstAmount = parseFloat(formValues.iGstAmount) || 0;

    // Determine which GST type is being used
    const cgstSgstActive = cGstAmount > 0 || sGstAmount > 0;
    const igstActive = iGstAmount > 0;

    let total = 0;
    if (cgstSgstActive) {
      total = invoiceAmount + cGstAmount + sGstAmount;
    } else if (igstActive) {
      total = invoiceAmount + iGstAmount;
    } else {
      total = invoiceAmount;
    }

    if (invoiceAmount > 0) {
      setValue('totalAmount', parseFloat(total.toFixed(2)));
    }
  }, [
    formValues.invoiceAmount,
    formValues.cGstAmount,
    formValues.sGstAmount,
    formValues.iGstAmount,
    setValue
  ]);

  const validateField = (fieldName, fieldCode, fieldType, value) => {
    if (!value && value !== 0) {
      return `${fieldName} is required`;
    }

    const mockEvent = {
      target: { value }
    };

    const validationResult = inputValidation(mockEvent, fieldCode, fieldType);

    if (validationResult === true) {
      return '';
    }

    return validationResult;
  };

  const validateInvoiceAmount = (value) => {
    const error = validateField('Invoice Amount', 'AMOUNT', 'AMOUNT_FIELD', value);
    if (!error && parseFloat(value) <= 0) {
      return 'Invoice Amount must be greater than 0';
    }
    return error;
  };

  const validateGstAmount = (value) => {
    if (value === '' || value === undefined) return '';

    if (parseFloat(value) < 0) {
      return 'GST Amount cannot be negative';
    }
    return '';
  };

  const validateTotalAmount = (value) => {
    const error = validateField('Total Amount', 'AMOUNT', 'AMOUNT_FIELD', value);
    if (!error && parseFloat(value) <= 0) {
      return 'Total Amount must be greater than 0';
    }
    return error;
  };

  const validateDueDate = (dueDate) => {
    if (!dueDate) {
      return 'Invoice Due Date is required';
    }

    const invoiceDate = formValues.invoiceDate;
    if (invoiceDate && new Date(dueDate) < new Date(invoiceDate)) {
      return 'Due Date must be on or after Invoice Date';
    }

    return '';
  };

  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  const validateForm = (data) => {
    const newErrors = {};

    newErrors.invoiceNumber = validateField(
      'Invoice Number',
      'FILE_NO',
      'TEXT_FIELD',
      data.invoiceNumber
    );

    newErrors.invoiceDate = data.invoiceDate ? '' : 'Invoice Date is required';

    newErrors.invoiceAmount = validateInvoiceAmount(data.invoiceAmount);

    newErrors.cGstAmount = validateGstAmount(data.cGstAmount);
    newErrors.iGstAmount = validateGstAmount(data.iGstAmount);
    newErrors.sGstAmount = validateGstAmount(data.sGstAmount);

    newErrors.totalAmount = validateTotalAmount(data.totalAmount);

    newErrors.invoiceDueDate = validateDueDate(data.invoiceDueDate);

    setValidationErrors(newErrors);

    return Object.values(newErrors).every((error) => !error);
  };

  const handleInvoiceGenerator = async (data) => {
    try {
      if (!validateForm(data)) {
        console.log('Form validation failed');
        return;
      }

      const cGstAmount = parseFloat(data.cGstAmount) || 0;
      const sGstAmount = parseFloat(data.sGstAmount) || 0;
      const iGstAmount = parseFloat(data.iGstAmount) || 0;

      // ✅ Logic: If CGST/SGST exist, set IGST to 0 and vice versa
      let finalCGst = 0;
      let finalSGst = 0;
      let finalIGst = 0;

      if (cGstAmount > 0 || sGstAmount > 0) {
        finalCGst = cGstAmount;
        finalSGst = sGstAmount;
        finalIGst = 0;
      } else if (iGstAmount > 0) {
        finalCGst = 0;
        finalSGst = 0;
        finalIGst = iGstAmount;
      }

      const credentials = {
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        receivableIds: receivableIds,
        invoice: {
          invoiceNo: data.invoiceNumber,
          invoiceDate: data.invoiceDate,
          netAmount: parseFloat(data.invoiceAmount),
          cGstAmount: finalCGst,
          iGstAmount: finalIGst,
          sGstAmount: finalSGst,
          partnerPayout: parseFloat(data.totalAmount),
          dueDate: data.invoiceDueDate
        }
      };

      const result = await dispatch(saveApiData(ReceiveApi?.generateInvoice, credentials));
      if (result) {
        // console.log('sss result invoice genereate', result);
        dispatch(setApiSuccess('Invoice generated successfully'));
        navigate('/receivable');
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      dispatch(setApiError('Something went wrong'));
    }
  };

  // ✅ Check if invoice amount is entered
  const invoiceAmountEntered = parseFloat(formValues.invoiceAmount) > 0;
  const cGstAmount = parseFloat(formValues.cGstAmount) || 0;
  const sGstAmount = parseFloat(formValues.sGstAmount) || 0;
  const iGstAmount = parseFloat(formValues.iGstAmount) || 0;

  // ✅ Check if both CGST and SGST are 0 (to enable IGST)
  const bothCgstSgstZero = cGstAmount === 0 && sGstAmount === 0;

  return (
    <Page title="Partner Management System || Receivable">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View File" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Scrollbar>
            <Typography variant="body1" className="headingLineUp headingLineUpOut">
              File Details
            </Typography>
            <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
              <TableContainer sx={{ overflow: 'auto', background: 'white' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="">Bank Name</StyledTableCell>
                      <StyledTableCell align="">File No</StyledTableCell>
                      <StyledTableCell align="">Borrower Name</StyledTableCell>
                      <StyledTableCell align="">Branch Name</StyledTableCell>
                      <StyledTableCell align="">Emp Class</StyledTableCell>
                      <StyledTableCell align="">Disb Date</StyledTableCell>
                      <StyledTableCell align="">Disb Amount</StyledTableCell>
                      <StyledTableCell align="">Loan Product Type</StyledTableCell>
                      <StyledTableCell align="">Invoice No</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedItem?.length > 0 &&
                      selectedItem.map((item, index) => (
                        <StyledTableRow key={`selectedInvoices-${index}`}>
                          <StyledTableCell align="left">
                            {isValidValue(item?.bankId)
                              ? normalizeValue(
                                  item?.bankName ? item?.bankName : `${NOT_AVAILABLE}`,
                                  NOT_AVAILABLE
                                )
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.fileNumber, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.borrowerName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.branchName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.empClass, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {isValidValue(item?.fileDisbursedDate)
                              ? fDate(item?.fileDisbursedDate)
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(
                              fIndianAmount(item?.loanAmountDisbursed),
                              NOT_AVAILABLE
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {isValidValue(item?.loanTypeId)
                              ? normalizeValue(
                                  item?.loanType ? item?.loanType : `${NOT_AVAILABLE}`,
                                  NOT_AVAILABLE
                                )
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {isValidValue(item?.invoiceNo) ? (
                              <RouterLink to="/receivable/invoice-generate">
                                {item?.invoiceNo}
                              </RouterLink>
                            ) : (
                              NOT_AVAILABLE
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Typography variant="body1" className="headingLineUp headingLineUpOut">
              Invoice Details
            </Typography>
            <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
              <form id="invoiceForm" onSubmit={handleReduxSubmit(handleInvoiceGenerator)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="invoiceNumber"
                      label="Invoice Number"
                      control={control}
                      errors={validationErrors}
                      isRequired
                      fieldCode="FILE_NO"
                      fieldType="TEXT_FIELD"
                      placeholder="Enter Invoice Number"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomDate
                      name="invoiceDate"
                      label="Invoice Date"
                      control={control}
                      errors={validationErrors}
                      isRequired
                      fieldCode="DATE"
                      fieldType="DATE_FIELD"
                      format="DD/MM/YYYY"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="invoiceAmount"
                      label="Invoice Amount"
                      control={control}
                      errors={validationErrors}
                      isRequired
                      fieldCode="AMOUNT"
                      fieldType="AMOUNT_FIELD"
                      type="number"
                      placeholder="Enter Invoice Amount"
                      isAmount
                    />
                  </Grid>

                  {/* ✅ CGST + SGST Group */}

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="cGstAmount"
                      label="CGST Amount"
                      control={control}
                      errors={validationErrors}
                      fieldCode="AMOUNT"
                      fieldType="AMOUNT_FIELD"
                      type="number"
                      placeholder="Enter CGST Amount"
                      isAmount
                      disabled={!invoiceAmountEntered}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="sGstAmount"
                      label="SGST Amount"
                      control={control}
                      errors={validationErrors}
                      fieldCode="AMOUNT"
                      fieldType="AMOUNT_FIELD"
                      type="number"
                      placeholder="Enter SGST Amount"
                      isAmount
                      disabled={!invoiceAmountEntered}
                    />
                  </Grid>

                  {/* ✅ IGST Group */}

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="iGstAmount"
                      label="IGST Amount"
                      control={control}
                      errors={validationErrors}
                      fieldCode="AMOUNT"
                      fieldType="AMOUNT_FIELD"
                      type="number"
                      placeholder="Enter IGST Amount"
                      isAmount
                      disabled={!bothCgstSgstZero || !invoiceAmountEntered}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="totalAmount"
                      label="Total Amount"
                      control={control}
                      errors={validationErrors}
                      fieldCode="AMOUNT"
                      fieldType="AMOUNT_FIELD"
                      type="number"
                      placeholder="Enter Total Amount"
                      isAmount
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomDate
                      name="invoiceDueDate"
                      label="Invoice Due Date"
                      control={control}
                      errors={validationErrors}
                      // isRequired
                      fieldCode="DATE"
                      fieldType="DATE_FIELD"
                      format="DD/MM/YYYY"
                    />
                  </Grid>
                </Grid>

                <Box className="" textAlign="right" mt={3}>
                  <Button type="submit" variant="contained" className="mainBtn">
                    Generate Invoice
                  </Button>
                </Box>
              </form>
            </Box>
          </Scrollbar>
        </Box>
      </Container>
    </Page>
  );
}
export default ViewMultipleFile;
